import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Fade from "@mui/material/Fade";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import NonprofitForm from "./Users/components/forms/NonprofitForm";
import AirportForm from "./Users/components/forms/AirportForm";
import { useAuth } from "context/AuthContext";
import {
  saveAirport,
  saveNonProfit,
  getAirportById,
  getNonProfitById,
} from "./Users/services/graphqlService";
import { canManageEntities } from "./missionOwnerUtils";

const ENTITY_TYPES = [
  {
    value: "NonProfit",
    label: "Non-Profit Organization",
    icon: "favorite",
    color: "success",
    description: "Register a non-profit organization for mission support",
  },
  {
    value: "Airport",
    label: "Airport",
    icon: "flight",
    color: "info",
    description: "Register an airport facility for mission coordination",
  },
];

// Mock users for first admin selection
const mockUsersForAdmin = [
  { id: 1, name: "John Doe", email: "john@example.com" },
  { id: 2, name: "Jane Smith", email: "jane@example.com" },
  { id: 3, name: "Mike Johnson", email: "mike@example.com" },
];

function AddEntityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isGuest, tokenEntityType } = useAuth();
  const canEditEntities = canManageEntities({ tokenEntityType, isGuest });

  const entityTypeParam = searchParams.get("entityType");
  const urlId = searchParams.get("id");
  const normalizeEntityTypeParam = (t) => {
    if (!t) return null;
    const s = (t || "").toLowerCase();
    if (s === "airport") return "Airport";
    if (s === "nonprofit") return "NonProfit";
    return t;
  };
  const [selectedType, setSelectedType] = useState(
    normalizeEntityTypeParam(entityTypeParam) || null
  );
  const [editId, setEditId] = useState(urlId || null);
  const [showForm, setShowForm] = useState(!!entityTypeParam);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  useEffect(() => {
    if (!canEditEntities) {
      navigate("/dashboard/entities", { replace: true });
    }
  }, [canEditEntities, navigate]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setTimeout(() => setShowForm(true), 100);
  };

  const handleBack = () => {
    setShowForm(false);
    setTimeout(() => {
      setSelectedType(null);
    }, 200);
  };

  const handleCancel = () => {
    navigate("/dashboard/entities");
  };

  // Fetch data for edit mode
  useEffect(() => {
    const fetchEntityData = async () => {
      if (!canEditEntities || !editId || !selectedType || !user?.id_token) {
        return;
      }

      setFetching(true);
      setError(null);

      try {
        if (selectedType === "Airport") {
          const result = await getAirportById(editId, user.id_token);
          if (result.success && result.data) {
            const airportData = result.data;
            const firstAddr = Array.isArray(airportData.address)
              ? airportData.address[0]
              : airportData.address;
            const airportAddress =
              typeof firstAddr === "object" && firstAddr
                ? firstAddr.address || ""
                : firstAddr || "";
            setInitialFormData({
              airportName: airportData.name || "",
              airportPhone: airportData.phone || "",
              city:
                airportData.city ||
                (typeof firstAddr === "object" ? firstAddr.city : "") ||
                "",
              state:
                airportData.state ||
                (typeof firstAddr === "object" ? firstAddr.state : "") ||
                "",
              address: airportAddress,
              airportEmail: airportData.email || "",
              airportUrl: airportData.url || "",
              airportUse: String(
                airportData.airportUse || airportData.category || ""
              )
                .trim()
                .toLowerCase(),
              airportOwnership: String(airportData.airportOwnership || "")
                .trim()
                .toLowerCase(),
              managerName: airportData.managerName || "",
              managerPhone: airportData.managerPhone || "",
              status: airportData.status || "active",
            });
          }
        } else if (selectedType === "NonProfit") {
          const result = await getNonProfitById(editId, user.id_token);
          if (result.success && result.data) {
            const nonprofitData = result.data;
            // Single address: API may return array or single address object
            let address = { address: "", city: "", state: "" };
            if (nonprofitData.address) {
              if (
                Array.isArray(nonprofitData.address) &&
                nonprofitData.address.length > 0
              ) {
                const first = nonprofitData.address[0];
                address = {
                  address: first.address || "",
                  city: first.city || "",
                  state: first.state || "",
                };
              } else if (
                typeof nonprofitData.address === "object" &&
                !Array.isArray(nonprofitData.address)
              ) {
                address = {
                  address: nonprofitData.address.address || "",
                  city: nonprofitData.address.city || "",
                  state: nonprofitData.address.state || "",
                };
              } else if (typeof nonprofitData.address === "string") {
                address = {
                  address: nonprofitData.address || "",
                  city: nonprofitData.city || "",
                  state: nonprofitData.state || "",
                };
              }
            } else if (nonprofitData.city || nonprofitData.state) {
              address = {
                address: "",
                city: nonprofitData.city || "",
                state: nonprofitData.state || "",
              };
            }
            const predefinedCategories = [
              "medical",
              "animal-rescue",
              "disaster-relief",
              "veteran-support",
              "conservation",
              "aviation-experience",
            ];
            const categoryValue = nonprofitData.category || "";
            const isPredefinedCategory =
              predefinedCategories.includes(categoryValue);

            setInitialFormData({
              name: nonprofitData.name || "",
              ein: nonprofitData.ein || "",
              category: isPredefinedCategory ? categoryValue : "other",
              customCategory: isPredefinedCategory ? "" : categoryValue,
              phoneNumber: nonprofitData.phone || "",
              address,
              operationalStates: Array.isArray(nonprofitData.operationalStates)
                ? nonprofitData.operationalStates
                : [],
              email: nonprofitData.email || "",
              websiteUrl: nonprofitData.url || "",
              description: nonprofitData.description || "",
              logo: nonprofitData.logo || null,
              status: nonprofitData.status || "active",
            });
          }
        }
      } catch (err) {
        console.error("Error fetching entity data:", err);
        const errorMsg = err.message || "Failed to load entity data";
        setError(errorMsg);
        // Error will be shown via subscription event if available
      } finally {
        setFetching(false);
      }
    };

    fetchEntityData();
  }, [canEditEntities, editId, selectedType, user?.id_token]);

  const handleSubmit = async (data) => {
    if (!canEditEntities) {
      navigate("/dashboard/entities", { replace: true });
      return;
    }

    if (!user?.id_token) {
      const authError = "Authentication required. Please sign in again.";
      setError(authError);
      // Dispatch error notification
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message: authError,
          },
        })
      );
      return;
    }

    // Show info notification when form is submitted
    // This will be automatically closed when success notification arrives
    const entityName = selectedType === "Airport" ? "Airport" : "Non-Profit";
    window.dispatchEvent(
      new CustomEvent("globalNotification", {
        detail: {
          type: "info",
          title: "Processing",
          message: `Saving ${entityName}... Please wait for confirmation.`,
          removeOnSuccess: true, // Flag to remove this when success arrives
        },
      })
    );

    setLoading(true);
    setError(null);

    try {
      if (selectedType === "Airport") {
        // Map form data to API format
        const airportData = {
          name: data.airportName,
          phone: data.airportPhone,
          city: data.city,
          state: data.state,
          address: data.address || data.street || "",
          email: data.airportEmail,
          url: data.airportUrl,
          airportUse: data.airportUse,
          airportOwnership: data.airportOwnership,
          managerName: data.managerName,
          managerPhone: data.managerPhone,
          status: data.status || "active",
        };

        const result = await saveAirport(airportData, user.id_token, editId);

        if (result.success) {
          // Success notification will come from subscription event
          // Redirect back to entity listing after a short delay
          setTimeout(() => {
            navigate("/dashboard/entities");
          }, 1500);
        } else {
          const errorMsg =
            result.message || "Failed to save airport. Please try again.";
          setError(errorMsg);
          // Error notification will come from subscription event if available
        }
      } else if (selectedType === "NonProfit") {
        // Map form data to API format — single address sent as one-element array
        const addr = data.address || {};
        const addressPayload = [
          {
            address: addr.address || "",
            city: addr.city || "",
            state: addr.state || "",
          },
        ];

        const categoryValue =
          data.category === "other"
            ? data.customCategory || ""
            : data.category || "";

        const nonprofitData = {
          name: data.name,
          ein: data.ein,
          category: categoryValue,
          phone: data.phoneNumber,
          address: addressPayload,
          city: addr.city || "",
          state: addr.state || "",
          operationalStates: Array.isArray(data.operationalStates)
            ? data.operationalStates
            : [],
          email: data.email,
          url: data.websiteUrl || "",
          description: data.description || "",
          logo:
            data.logo && typeof data.logo === "string"
              ? data.logo
              : data.logo
              ? ""
              : "",
          status: data.status || "active",
        };

        const result = await saveNonProfit(
          nonprofitData,
          user.id_token,
          editId
        );

        if (result.success) {
          // Success notification will come from subscription event
          // Redirect back to entity listing after a short delay
          setTimeout(() => {
            navigate("/dashboard/entities");
          }, 1500);
        } else {
          const errorMsg =
            result.message || "Failed to save non-profit. Please try again.";
          setError(errorMsg);
          // Error notification will come from subscription event if available
        }
      }
    } catch (err) {
      console.error("Error saving entity:", err);
      const errorMsg =
        err.message || "Failed to save entity. Please try again.";
      setError(errorMsg);
      // Error notification will come from subscription event if available
    } finally {
      setLoading(false);
    }
  };

  const selectedEntityConfig = ENTITY_TYPES.find(
    (t) => t.value === selectedType
  );

  if (!canEditEntities) {
    return null;
  }

  return (
    <SoftBox py={2}>
      {/* Header */}
      <SoftBox mb={2}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold">
              {selectedType && selectedEntityConfig
                ? `Add ${selectedEntityConfig.label}`
                : "Add Entity"}
            </SoftTypography>

            {selectedType && selectedEntityConfig ? (
              <SoftTypography variant="body2" color="text">
                {selectedEntityConfig.description}
              </SoftTypography>
            ) : (
              <SoftTypography variant="body2" color="text">
                Choose the type of entity you want to register
              </SoftTypography>
            )}
          </SoftBox>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            disabled={loading || fetching}
          >
            <Icon sx={{ mr: 1 }}>arrow_back</Icon>
            Back to Entities
          </SoftButton>
        </SoftBox>
      </SoftBox>

      {/* Entity Type Selection */}
      {!selectedType && (
        <Fade in={!selectedType} timeout={300}>
          <SoftBox>
            <Card sx={{ p: 3 }}>
              <SoftBox mb={3} textAlign="center">
                <SoftTypography variant="h4" fontWeight="medium" mb={0.5}>
                  Select Entity Type
                </SoftTypography>
                <SoftTypography variant="body2" color="text">
                  Choose the type of entity you want to register in the system
                </SoftTypography>
              </SoftBox>

              <SoftBox
                display="flex"
                gap={3}
                justifyContent="center"
                flexWrap="wrap"
                maxWidth={800}
                mx="auto"
              >
                {ENTITY_TYPES.map((type) => (
                  <Card
                    key={type.value}
                    sx={{
                      width: "100%",
                      maxWidth: 360,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      border: "2px solid transparent",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: 8,
                        borderColor: `${type.color}.main`,
                      },
                    }}
                    onClick={() => handleTypeSelect(type.value)}
                  >
                    <SoftBox p={3} textAlign="center">
                      <SoftBox
                        bgColor={type.color}
                        variant="gradient"
                        width="4rem"
                        height="4rem"
                        borderRadius="xl"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                          boxShadow: 4,
                        }}
                      >
                        <Icon
                          fontSize="large"
                          sx={{ color: "white", fontSize: "2rem" }}
                        >
                          {type.icon}
                        </Icon>
                      </SoftBox>

                      <SoftTypography variant="h5" fontWeight="bold" mb={1}>
                        {type.label}
                      </SoftTypography>

                      <SoftTypography variant="body2" color="text" mb={2}>
                        {type.description}
                      </SoftTypography>

                      <SoftButton
                        variant="gradient"
                        color={type.color}
                        fullWidth
                        size="large"
                      >
                        <Icon sx={{ mr: 1 }}>arrow_forward</Icon>
                        Select & Continue
                      </SoftButton>
                    </SoftBox>
                  </Card>
                ))}
              </SoftBox>

              <SoftBox mt={2} textAlign="center">
                <SoftTypography variant="caption" color="text">
                  You can add multiple entities of each type. Select one to get
                  started.
                </SoftTypography>
              </SoftBox>
            </Card>
          </SoftBox>
        </Fade>
      )}

      {/* Error/Success Messages */}
      {error && (
        <SoftBox mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </SoftBox>
      )}

      {/* Form Display */}
      {selectedType && showForm && !fetching && (
        <Fade in={showForm} timeout={300}>
          <Card>
            <SoftBox p={2.5}>
              {selectedType === "NonProfit" && (
                <NonprofitForm
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  availableUsers={mockUsersForAdmin}
                  initialData={initialFormData}
                  loading={loading}
                />
              )}

              {selectedType === "Airport" && (
                <AirportForm
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  initialData={initialFormData}
                  loading={loading}
                />
              )}
            </SoftBox>
          </Card>
        </Fade>
      )}
    </SoftBox>
  );
}

export default AddEntityPage;
